/* eslint-disable jsx-a11y/anchor-is-valid */
import React, { useEffect, useMemo, useState } from "react";
import { NavLink, useParams, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';

import {
  userAction,
  gamesAction,
  brandAction,
  shopAction,
} from "@redux/modules";  

// Header Footer
import Header from '../Layout/Header';
import Footer from '../Layout/Footer';
import TitleBackground from "@components/TitleBackground";  

// Import Swiper React components
import { Swiper, SwiperSlide } from 'swiper/react';

// Import Swiper styles
import "swiper/css";
import "swiper/css/free-mode";
import "swiper/css/navigation";
import "swiper/css/thumbs";

import "./styles.scss";

// import required modules
import { FreeMode, Navigation, Thumbs } from 'swiper/modules';

import ScoreHuntBanner from "@assets/images/games/score-hunt/score-hunt-icon.svg";
import {
  FaHeart,
  FaStar,
  FaMinus,
  FaPlus,
  FaChevronLeft,
  FaChevronRight,
} from 'react-icons/fa';

// Toastify
import showErrMessage from "@components/ShowErrMessage";
import showProductMessage from "@components/showProductMessage";

// Utils
import { linkifyText } from "@utils";

const BrandProductDetail = () => {
  const dispatch = useDispatch();
  const loginSettings = useSelector((store) => store.auth.loginSettings);
  const { brandSubCategoryId, brandStyleId } = useParams();
  const cartData = useSelector((store) => store.cart.cartData);
  const brandSalesMode = useSelector((store) => store.brand.brandSalesMode);

  // BRAND_PRODUCTS_SALES_MODE: true => satış açık (mevcut görünüm),
  // false => satış kapalı (fiyat/stok/sepete ekle gizli, bayi bilgisi gösterilir).
  // Yüklenirken (null) mevcut görünüm korunur.
  const isSalesEnabled = brandSalesMode !== false;

  const [thumbsSwiper, setThumbsSwiper] = useState(null);
  const [productCount, setProductCount] = useState(1);

  // Store
  const brandStyleActive = useSelector((store) => store.brand.brandStyleActive);
  const brandStyleList = useSelector((store) => store.brand.brandStyleList);
  // Game Score Hunt
  const scoreHunt = useSelector((store) => store.games.scoreHunt);

  // Game States
  const [showGame, setShowGame] = useState(false);
  const [availableGame, setAvailableGame] = useState(false);

  // Active BrandStyle
  const [activeBrandStyle, setActiveBrandStyle] = useState(null);

  const navigate = useNavigate();

  useEffect(() => {
    if (brandSubCategoryId) {
      dispatch(brandAction.getBrandSubCategoryStyleList(brandSubCategoryId));
    }
    return () => {};
  }, [brandSubCategoryId, dispatch]);

  useEffect(() => {
    // Get All Brand Style List (tüm kategorilerdeki ürünler - hem "Diğer Ürünler"
    // bölümü hem de ürünler arası gezinme butonları bu listeyi kullanır)
    dispatch(brandAction.getBrandStyleList(0, 2000));

    // Marka ürünleri satış modu (fiyat/stok/sepet gösterilsin mi?)
    dispatch(brandAction.getBrandProductsSalesMode());

    // Score Hunt
    dispatch(gamesAction.scoreHunt());

    // Score Hunt Control
    dispatch(gamesAction.scoreHuntControl()).then((res) => {
      if (res.isHttpSuccess) {
        if (res.data.remainTime > 0) {
          setAvailableGame(true);
        }
      }
    });

    return () => {};
  }, [dispatch]);

  useEffect(() => {
    // Puan Avı logosu yalnızca scoreHunt'ın atandığı ürün açıkken görünmeli.
    // Slider ile başka ürüne geçildiğinde eşleşme yoksa gizle.
    if (
      scoreHunt &&
      parseInt(scoreHunt?.brandStyleId) === parseInt(brandStyleId)
    ) {
      setShowGame(true);
    } else {
      setShowGame(false);
    }
  }, [scoreHunt, brandStyleId]);

  useEffect(() => {
    if (!brandStyleId) {
      return () => {};
    }

    const idNum = parseInt(brandStyleId);

    // Öncelik: aktif alt kategori listesindeki tam detaylı ürün
    const fromSubCategory = brandStyleActive?.content?.find(
      (style) => style.id === idNum
    );

    if (fromSubCategory) {
      setActiveBrandStyle(fromSubCategory);
      return () => {};
    }

    // Kategoriler arası geçişte alt kategori listesi henüz yüklenmediyse,
    // içeriğin boşalıp mavi başlık alanının küçülmemesi için ürünü zaten
    // elimizde olan tüm ürün listesinden anlık gösteririz (detay gelince güncellenir).
    const fromAllList = brandStyleList?.content?.find(
      (style) => style.id === idNum
    );

    if (fromAllList) {
      setActiveBrandStyle(fromAllList);
    }

    return () => {};
  }, [brandStyleId, brandStyleActive, brandStyleList]);

  const handleAddFavourite = (e, activeBrandStyleId) => {
    e.preventDefault();

    if (activeBrandStyleId) {
      dispatch(brandAction.setFavouriteBrandStyle(activeBrandStyleId)).then(
        (res) => {
          if (res.isHttpSuccess) {
            dispatch(
              brandAction.getBrandSubCategoryStyleList(brandSubCategoryId)
            );
          }
        }
      );
    }
  };

  const handleRemoveFavourite = (e, activeBrandStyleId) => {
    e.preventDefault();

    if (activeBrandStyleId) {
      dispatch(brandAction.deleteFavouriteBrandStyle(activeBrandStyleId)).then(
        (res) => {
          if (res.isHttpSuccess) {
            dispatch(
              brandAction.getBrandSubCategoryStyleList(brandSubCategoryId)
            );
          }
        }
      );
    }
  };

  const handlePlus = (e, stock) => {
    e.preventDefault();

    if (productCount + 1 <= stock) {
      setProductCount(productCount + 1);
    }
  };

  const handleMinus = (e) => {
    e.preventDefault();
    if (productCount - 1 >= 1) {
      setProductCount(productCount - 1);
    }
  };

  const addToCart = (e, productId) => {
    e.preventDefault();

    let count = parseInt(productCount);

    if (cartData?.length > 0) {
      for (const item of cartData?.cartProductDTOList) {
        if (item?.product?.id === productId) {
          count += parseInt(item?.productCount);
        }
      }
    }

    // Ürün adedinin mevcut stok miktarını aşıp aşmadığını kontrol eder
    if (count > activeBrandStyle.stock) {
      showErrMessage(
        `Stok adedi ${activeBrandStyle.stock}'tir. Sadece ${activeBrandStyle.stock} ürün ekleyebilirsiniz.`
      );
      return;
    }

    dispatch(brandAction.addToCart(activeBrandStyle.id, count))
      .then((e) => {
        if (e.isHttpSuccess) {
          dispatch(shopAction.getBrandCartList());
          dispatch(shopAction.getCart());
          showProductMessage(`Ürün sepete eklendi!`, "/brand-cart");
        }
      })
      .catch((err) => {
        console.log("err: ", err);
        showErrMessage("Ürün sepete eklenirken bir hata oluştu!");
      });
  };

  const handleScoreHuntClick = (e) => {
    e.preventDefault();
    navigate("/games/score-hunt", {
      state: { fromAboutPage: true },
      replace: true
    });
  }

  // Ürünler arası gezinme: tüm kategorilerdeki ürün listesi üzerinde önceki/sonraki
  // ürüne geçer (alt kategori sınırı yok). Son ürüne gelip "sonraki"ye basılırsa
  // başa, ilk üründe "önceki"ye basılırsa sona döner (döngüsel).
  const brandStyleItems = brandStyleList?.content || [];
  const currentIndex = brandStyleItems.findIndex(
    (style) => style.id === parseInt(brandStyleId)
  );

  const navigateToBrandStyle = (targetIndex) => {
    const total = brandStyleItems.length;
    if (total === 0) {
      return;
    }
    const nextIndex = ((targetIndex % total) + total) % total;
    const target = brandStyleItems[nextIndex];
    if (target) {
      setProductCount(1);
      // Ürünler arası geçişte sayfa başa kaydırılmasın (bkz. ScrollToTop).
      navigate(
        `/brand-product-detail/${target.brandSubCategoryId}/${target.id}`,
        { state: { preserveScroll: true } }
      );
    }
  };

  const handlePrevProduct = (e) => {
    e.preventDefault();
    navigateToBrandStyle(currentIndex - 1);
  };

  const handleNextProduct = (e) => {
    e.preventDefault();
    navigateToBrandStyle(currentIndex + 1);
  };

  // "Diğer Ürünler": breadcrumb'da gösterilen kategoriyle (alt kategori) aynı
  // kategorideki diğer ürünlerden rastgele en fazla 4 tanesi. Mevcut ürün hariç
  // tutulur. useMemo sayesinde liste yalnızca ürün/kategori değişince yeniden
  // karılır (her render'da değil), böylece kartlar gereksiz yere titremez.
  const otherProducts = useMemo(() => {
    const currentId = parseInt(brandStyleId);
    const subCategoryId = parseInt(brandSubCategoryId);

    const sameCategory = (brandStyleList?.content || []).filter(
      (item) =>
        item.id !== currentId && item.brandSubCategoryId === subCategoryId
    );

    // Fisher-Yates karıştırma
    const shuffled = [...sameCategory];
    for (let i = shuffled.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
    }

    return shuffled.slice(0, 4);
  }, [brandStyleList, brandStyleId, brandSubCategoryId]);

  return (
    <>
      <Header />

      <main>
        <section
          className="position-relative pt-sm-5 pt-3 mb-sm-10 mb-5"
          data-title-background="pt"
        >
          <TitleBackground />
          <div className="container">
            <div
              className="d-lg-flex d-none text-white small mb-4 opacity-50 position-relative"
              style={{ zIndex: 2 }}
              data-title-background
            >
              <NavLink
                to="/home"
                className="text-white text-decoration-none me-3"
              >
                Anasayfa
              </NavLink>
              <span className="me-3">»</span>
              <NavLink
                to="/brands"
                className="text-white text-decoration-none me-3"
              >
                Ürünlerimiz
              </NavLink>
              {activeBrandStyle?.brandSubCategoryName && (
                <>
                  <span className="me-3">»</span>
                  <NavLink
                    to="/brands"
                    state={{
                      brandSubCategoryId: activeBrandStyle.brandSubCategoryId,
                      brandSubCategoryName:
                        activeBrandStyle.brandSubCategoryName,
                    }}
                    className="text-white text-decoration-none me-3"
                  >
                    {activeBrandStyle.brandSubCategoryName}
                  </NavLink>
                </>
              )}
            </div>

            <div className="position-relative">
              {/* Ürünler arası gezinme butonları (döngüsel) */}
              {brandStyleItems.length > 1 && (
                <>
                  <button
                    type="button"
                    onClick={handlePrevProduct}
                    aria-label="Önceki ürün"
                    className="btn btn-blue text-white rounded-circle p-0 d-none d-lg-flex align-items-center justify-content-center shadow position-absolute top-50 translate-middle-y z-index-top"
                    style={{
                      width: "44px",
                      height: "44px",
                      left: "-56px",
                    }}
                  >
                    <FaChevronLeft />
                  </button>

                  <button
                    type="button"
                    onClick={handleNextProduct}
                    aria-label="Sonraki ürün"
                    className="btn btn-blue text-white rounded-circle p-0 d-none d-lg-flex align-items-center justify-content-center shadow position-absolute top-50 translate-middle-y z-index-top"
                    style={{
                      width: "44px",
                      height: "44px",
                      right: "-56px",
                    }}
                  >
                    <FaChevronRight />
                  </button>
                </>
              )}

              {/* Mobil/tablet: ürünler arası gezinme. Masaüstündeki yan butonlar
                  dar ekranda kenarlara sığmadığı için ekranın solunda/sağında,
                  dikeyde ortalı ve sayfa kaydırılsa da sabit duran küçük butonlar. */}
              {brandStyleItems.length > 1 && (
                <>
                  <button
                    type="button"
                    onClick={handlePrevProduct}
                    aria-label="Önceki ürün"
                    className="brand-product-mobile-nav prev btn btn-blue text-white rounded-circle p-0 d-flex d-lg-none align-items-center justify-content-center shadow"
                  >
                    <FaChevronLeft />
                  </button>

                  <button
                    type="button"
                    onClick={handleNextProduct}
                    aria-label="Sonraki ürün"
                    className="brand-product-mobile-nav next btn btn-blue text-white rounded-circle p-0 d-flex d-lg-none align-items-center justify-content-center shadow"
                  >
                    <FaChevronRight />
                  </button>
                </>
              )}

              <div className="row gy-5">
                <div className="col-lg-5">
                  {/* Main Slider */}
                  <div className="position-relative bg-white rounded-3 overflow-hidden mb-2">
                    <Swiper
                      spaceBetween={0}
                      navigation={true}
                      thumbs={{ swiper: thumbsSwiper }}
                      modules={[FreeMode, Navigation, Thumbs]}
                      className="swiper-product-slider rounded-3"
                    >
                      {activeBrandStyle?.brandStyleContentDTOList?.length >
                      0 ? (
                        activeBrandStyle?.brandStyleContentDTOList?.map(
                          (item) => {
                            return (
                              <SwiperSlide key={item.id}>
                                <a
                                  href={item.baseAddress}
                                  className="d-block"
                                  data-fancybox
                                >
                                  <img
                                    src={item.baseAddress}
                                    alt={activeBrandStyle?.name}
                                    className="w-100 img-cover"
                                  />
                                </a>
                              </SwiperSlide>
                            );
                          }
                        )
                      ) : (
                        <SwiperSlide>
                          <div style={{ background: "#000", width: "100%" }}>
                            {loginSettings?.logo && (
                              <img
                                src={loginSettings?.logo}
                                alt=""
                                className="w-100 opacity-50"
                                style={{
                                  height: "200px",
                                  objectFit: "scale-down",
                                }}
                              />
                            )}
                          </div>
                        </SwiperSlide>
                      )}
                    </Swiper>
                    {/* End Main Slider */}

                    {/* Thumnnail Slider */}
                    <Swiper
                      onSwiper={setThumbsSwiper}
                      spaceBetween={10}
                      slidesPerView={3}
                      freeMode={true}
                      watchSlidesProgress={true}
                      modules={[FreeMode, Navigation, Thumbs]}
                      className="swiper-product-thumbs-slider"
                    >
                      {activeBrandStyle?.brandStyleContentDTOList?.length >
                      0 ? (
                        activeBrandStyle?.brandStyleContentDTOList?.map(
                          (item) => {
                            return (
                              <SwiperSlide key={item.id}>
                                {item.baseAddress ? (
                                  <img
                                    src={item.baseAddress}
                                    alt={activeBrandStyle?.name}
                                    className="rounded-3 border-blue-lighter"
                                    style={{
                                      height: "100px",
                                      objectFit: "contain",
                                    }}
                                  />
                                ) : (
                                  <div
                                    style={{
                                      height: "200px",
                                      background: "#000",
                                    }}
                                  >
                                    {loginSettings?.logo && (
                                      <img
                                        src={loginSettings?.logo}
                                        alt=""
                                        className="w-100 opacity-50"
                                        style={{
                                          height: "200px",
                                          objectFit: "scale-down",
                                        }}
                                      />
                                    )}
                                  </div>
                                )}
                              </SwiperSlide>
                            );
                          }
                        )
                      ) : (
                        <SwiperSlide>
                          <div style={{ height: "200px", background: "#000" }}>
                            {loginSettings?.logo && (
                              <img
                                src={loginSettings?.logo}
                                alt=""
                                className="w-100 opacity-50"
                                style={{
                                  height: "200px",
                                  objectFit: "scale-down",
                                }}
                              />
                            )}
                          </div>
                        </SwiperSlide>
                      )}
                    </Swiper>
                    {/* End Thumnnail Slider */}

                    {activeBrandStyle?.topIcon &&
                      activeBrandStyle?.topIconText && (
                        <span className="rounded-pill bg-orange text-white small position-absolute z-index-top top-0 start-0 px-3 py-1 m-sm-3 m-2">
                          {activeBrandStyle?.topIconText}
                        </span>
                      )}
                  </div>
                </div>

                {/* Product Content */}
                {activeBrandStyle && (
                  <div className="col-lg-5 offset-lg-1 offset-0">
                    {/* ScoreHunt Game */}
                    {showGame && availableGame ? (
                      <h1
                        className="mb-5 text-white text-white-lg"
                        data-title-background
                      >
                        {activeBrandStyle.name && activeBrandStyle.name}
                        <a
                          href="#"
                          onClick={(e) => handleScoreHuntClick(e)}
                          className="w-auto d-flex align-items-center pt-3 text-decoration-none question-mark-title"
                        >
                          <img
                            src={ScoreHuntBanner}
                            alt=""
                            className="bg-white p-1 rounded-circle shadow-lg"
                            style={{
                              width: "48px",
                              height: "48px",
                              cursor: "pointer",
                            }}
                          />
                          <h4 className="m-0 ms-2">Puan Avı</h4>
                        </a>
                      </h1>
                    ) : (
                      <h1
                        className="mb-5 text-white text-white-lg"
                        data-title-background
                      >
                        {activeBrandStyle.name && activeBrandStyle.name}
                      </h1>
                    )}
                    {/* End ScoreHunt Game */}

                    <div className="pt-lg-5 pt-0">
                      <div className="mb-4">
                        {isSalesEnabled && activeBrandStyle.stock && (
                          <span className="text-blue small mb-2 d-block">
                            {activeBrandStyle.stock} Stok
                          </span>
                        )}

                        {isSalesEnabled && activeBrandStyle.price && (
                          <span className="h2 text-green mb-4 d-block fw-bold">
                            {activeBrandStyle.price} ₺
                          </span>
                        )}

                        {isSalesEnabled && (
                          <>
                            <div className="h4 text-gray mb-2 d-block fw-bold">
                              {activeBrandStyle.rate >= 1 ? (
                                <FaStar className="text-yellow" />
                              ) : (
                                <FaStar />
                              )}

                              {activeBrandStyle.rate >= 2 ? (
                                <FaStar className="text-yellow" />
                              ) : (
                                <FaStar />
                              )}

                              {activeBrandStyle.rate >= 3 ? (
                                <FaStar className="text-yellow" />
                              ) : (
                                <FaStar />
                              )}

                              {activeBrandStyle.rate >= 4 ? (
                                <FaStar className="text-yellow" />
                              ) : (
                                <FaStar />
                              )}

                              {activeBrandStyle.rate === 5 ? (
                                <FaStar className="text-yellow" />
                              ) : (
                                <FaStar />
                              )}
                            </div>

                            <span className="text-gray small mb-2 d-block">
                              {activeBrandStyle.countOfRate} Değerlendirme
                            </span>
                          </>
                        )}

                        <div>
                          {activeBrandStyle.favouriteStatus ? (
                            <a
                              href="#"
                              className="text-gray"
                              onClick={(e) =>
                                handleRemoveFavourite(e, activeBrandStyle.id)
                              }
                            >
                              <FaHeart style={{ fill: "red", color: "red" }} />
                              <span
                                className="d-inline-block"
                                style={{ marginLeft: "5px" }}
                              >
                                Vazgeç
                              </span>
                            </a>
                          ) : (
                            <a
                              href="#"
                              className="text-gray text-decoration-none"
                              onClick={(e) =>
                                handleAddFavourite(e, activeBrandStyle.id)
                              }
                            >
                              <FaHeart />
                              <span
                                className="d-inline-block"
                                style={{ marginLeft: "5px" }}
                              >
                                Beğen
                              </span>
                            </a>
                          )}
                        </div>

                        {isSalesEnabled && (
                          <div className="mt-3 d-flex align-items-center justify-content-between">
                            <div>
                              <a href="#" onClick={(e) => handleMinus(e)}>
                                <FaMinus
                                  style={{ fill: "#000", color: "#000" }}
                                />
                              </a>

                              <input
                                type="text"
                                value={productCount}
                                maxLength={3}
                                className="counter"
                                onChange={(e) => {
                                  const value = e.target.value;
                                  const parsedValue = parseInt(value, 10);

                                  if (value === "" || isNaN(parsedValue)) {
                                    setProductCount(value);
                                  } else if (
                                    parsedValue > activeBrandStyle.stock
                                  ) {
                                    setProductCount(activeBrandStyle.stock);
                                  } else {
                                    setProductCount(parsedValue);
                                  }
                                }}
                                onBlur={(e) => {
                                  const value = parseInt(e.target.value, 10);
                                  if (isNaN(value) || value < 1) {
                                    setProductCount(1);
                                  } else if (value > activeBrandStyle.stock) {
                                    setProductCount(activeBrandStyle.stock);
                                  } else {
                                    setProductCount(value);
                                  }
                                }}
                              />

                              <a
                                href="#"
                                onClick={(e) =>
                                  handlePlus(e, activeBrandStyle.stock)
                                }
                              >
                                <FaPlus
                                  style={{ fill: "#000", color: "#000" }}
                                />
                              </a>
                            </div>

                            <a
                              href="#"
                              className="btn btn-green text-white rounded-pill py-sm-3 py-2 px-sm-4 px-3 text-nowrap"
                              onClick={(e) => addToCart(e, activeBrandStyle.id)}
                            >
                              <i className="fas fa-shopping-cart me-3 d-sm-inline-block d-none"></i>
                              Sepete Ekle
                            </a>
                          </div>
                        )}
                      </div>

                      {activeBrandStyle.showProductCode === true &&
                        activeBrandStyle.productCode && (
                          <div className="row row-cols-2 g-0 align-items-center small">
                            <div className="col py-3 border-bottom">
                              Ürün Kodu
                            </div>
                            <div className="col py-3 border-bottom">
                              {activeBrandStyle.productCode}
                            </div>
                          </div>
                        )}

                      {activeBrandStyle.brandStylePropertyDTOList?.length > 0 &&
                        activeBrandStyle.brandStylePropertyDTOList.map(
                          (item) => (
                            <div
                              className="row row-cols-2 g-0 align-items-center small"
                              key={item.id}
                            >
                              <div className="col py-3 border-bottom">
                                {item.propertyName}
                              </div>
                              <div className="col py-3 border-bottom">
                                {item.propertyValue}
                              </div>
                            </div>
                          )
                        )}

                      {activeBrandStyle.description && (
                        <p className="mt-4 fw-light">
                          {linkifyText(activeBrandStyle.description)}
                        </p>
                      )}
                    </div>
                  </div>
                )}
                {/* End Product Content */}
              </div>
            </div>
          </div>
        </section>

        {/* Other Brands */}
        <section className="mb-sm-10 mb-5">
          <div className="container">
            <h2 className="text-center mb-sm-8 mb-5">Diğer Ürünler</h2>
            <div className="row g-sm-4 g-2">
              {otherProducts.length > 0
                ? otherProducts.map((item) => {
                      return (
                        <div className="col-xl-3 col-6" key={item.id}>
                          <div className="bg-white text-decoration-none shadow overflow-hidden rounded-3 h-100 d-flex flex-column">
                            {item.brandStyleContentDTOList?.length > 0 &&
                            item.name ? (
                              <img
                                src={
                                  item.brandStyleContentDTOList[0]?.baseAddress
                                }
                                alt={item.name}
                                className="img-contain w-100 p-3"
                                style={{ width: "300px", height: "200px" }}
                              />
                            ) : (
                              <div
                                style={{ background: "#000", width: "100%" }}
                              >
                                {loginSettings?.logo && (
                                  <img
                                    src={loginSettings?.logo}
                                    alt=""
                                    className="w-100 opacity-50"
                                    style={{
                                      height: "200px",
                                      objectFit: "scale-down",
                                    }}
                                  />
                                )}
                              </div>
                            )}

                            <div className="p-sm-4 p-3 flex-grow-1 d-flex flex-column justify-content-between">
                              {item.name && (
                                <h5 className="fw-normal lh-base mb-2">
                                  {item.name}
                                </h5>
                              )}

                              <span
                                className="d-block small text-black mb-4"
                                dangerouslySetInnerHTML={{
                                  __html:
                                    item.description?.length > 120
                                      ? item.description.substring(0, 120) +
                                        "..."
                                      : item.description,
                                }}
                              />
                              <NavLink
                                className="btn btn-green text-white w-100 rounded-pill py-sm-3 py-2 px-sm-4 px-3 text-nowrap mt-auto"
                                to={{
                                  pathname: `/brand-product-detail/${item.brandSubCategoryId}/${item.id}`,
                                  brandStyleId: item.id,
                                }}
                              >
                                İncele
                                <i className="fas fa-arrow-right ms-3 d-sm-inline-block d-none" />
                              </NavLink>
                            </div>
                          </div>
                        </div>
                      );
                    })
                : "Ürün Bulunmuyor!"}
            </div>
          </div>
        </section>
        {/* End Other Brands */}
      </main>

      <Footer />
    </>
  );
};

export default BrandProductDetail;
